import axios, { AxiosInstance } from 'axios';
import { IndexedDbRepository } from '@/repositories/indexedDbRepository';
import { convertSnakeToCamel } from '@/utils';

const baseURL = `${import.meta.env.VITE_TBS_SERVER_BASE_URL}`;
const CACHED_STATUS = 304;

export const tbsJsonAxiosClientController = new AbortController();
const signal = tbsJsonAxiosClientController.signal;

/**
 * TBS中間サーバー 接続用 Axios インスタンス
 */
const repository: AxiosInstance = axios.create({ baseURL, signal });
const indexedDb = IndexedDbRepository();

const __DEBUG__ = import.meta.env.MODE !== 'prod';

repository.interceptors.request.use(
  async (config) => {
    if (config.method !== 'get' || !config.url) return config;

    const cached = await indexedDb.get(config.url);
    if (cached) {
      // IndexedDBからエンドポイントごとの最終更新日時を取得
      const lastModified = cached.lastModified;
      // 更新日時が異なるかどうか判定設定を加える
      if (lastModified && config.headers) config.headers['If-Modified-Since'] = lastModified;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

repository.interceptors.response.use(
  async (response) => {
    if (__DEBUG__) {
      console.debug(
        `[${response.config.method?.toUpperCase()}] ${response.config.url} => ${response.status}`,
        convertSnakeToCamel(response.data)
      );
    }

    if (response.data) {
      convertSnakeToCamel(response.data);
    }

    // データを永続化
    if (response.config.method === 'get' && response.config.url) {
      await indexedDb.save({
        key: response.config.url,
        data: response.data,
        lastModified: response.headers['last-modified'],
      });
    }

    // 先月以前の番組表がキャッシュされていた場合は削除
    const keys = await indexedDb.getAllKeys();
    keys?.forEach((key) => {
      if (indexedDb.isExpiredProgramsKey(key)) indexedDb.remove(key);
    });

    return response;
  },
  async (error) => {
    // 304 Not Modifiedの場合、IndexedDBに永続化したレスポンスを返す
    // キャッシュは確実に存在する為、resolveとしてreturn
    if (error?.response?.status === CACHED_STATUS) {
      const cachedResponse = await indexedDb.get(error?.response.config.url);
      return Promise.resolve(cachedResponse);
    }
    return Promise.reject(error);
  }
);

export default repository;
